import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";

/** Copper-tinted card variants keyed by product slug — mirrors the
 * "urban-money" style backgrounds from the CorpZo Fund Ops source. */
const PRODUCT_STYLE = {
  "home-loan":               { tint: "linear-gradient(135deg,#FFF8E7,#F7E7C4)", accent: "#B8860B" },
  "car-loan":                { tint: "linear-gradient(135deg,#F1E9DA,#E5D2B0)", accent: "#7A4E1D" },
  "personal-loan":           { tint: "linear-gradient(135deg,#FBF3E4,#F1D9AF)", accent: "#8A6600" },
  "business-loan":           { tint: "linear-gradient(135deg,#F5EFE4,#E8D9B9)", accent: "#5D3B15" },
  "lap":                     { tint: "linear-gradient(135deg,#F8EEDE,#EAD7AB)", accent: "#7A4E1D" },
  "working-capital":         { tint: "linear-gradient(135deg,#F4EDDE,#E4CFAB)", accent: "#B8860B" },
  "cc-od":                   { tint: "linear-gradient(135deg,#F9F1DE,#EED7AB)", accent: "#8A6600" },
  "term-loan":               { tint: "linear-gradient(135deg,#F0E7D2,#D7BE94)", accent: "#5D3B15" },
  "equipment-finance":       { tint: "linear-gradient(135deg,#F4EAD2,#DCC28F)", accent: "#7A4E1D" },
  "project-finance":         { tint: "linear-gradient(135deg,#EFE4CE,#D2B588)", accent: "#8A6600" },
  "construction-finance":    { tint: "linear-gradient(135deg,#F8F0DD,#EBD3A6)", accent: "#B8860B" },
  "supply-chain-finance":    { tint: "linear-gradient(135deg,#F3EAD5,#D9BE8A)", accent: "#5D3B15" },
  "invoice-discounting":     { tint: "linear-gradient(135deg,#F6EEDA,#E1CA96)", accent: "#7A4E1D" },
  "loan-against-securities": { tint: "linear-gradient(135deg,#F9F0DA,#E9CE97)", accent: "#8A6600" },
  "structured-finance":      { tint: "linear-gradient(135deg,#F1E5CE,#D4B686)", accent: "#B8860B" },
  "private-credit":          { tint: "linear-gradient(135deg,#EEE2C6,#CFAE78)", accent: "#5D3B15" },
};
const DEFAULT_STYLE = { tint: "linear-gradient(135deg,#F5EFE4,#E8D9B9)", accent: "#7A4E1D" };

const fmtINR = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 1)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${(n / 1000).toFixed(0)} K`;
};

export default function DebtProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/products").then((r) => {
      setProducts(r.data?.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen text-ink bg-cream" data-testid="debt-products-page">
      <Seo title="Loan Products · VenturaZ MONEY" description="15 debt products for individuals & businesses — home / business / working capital / structured credit / private credit." path="/venturaz-money/products" />
      <DebtNavbar />
      <section className="max-w-6xl mx-auto px-6 py-14 lg:py-20">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">Loan Products</div>
        <h1 className="font-display text-3xl md:text-4xl mb-3 leading-tight">15 loan products · one honest advisor.</h1>
        <p className="text-ink-soft max-w-2xl mb-10">Individual and business credit — from a ₹50k personal loan to a ₹500 Cr structured line. Pick a product below to see rates, tenure, docs and the lenders we tap.</p>
        {loading && <div className="text-sm text-ink-muted">Loading…</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="debt-products-grid">
          {products.map((p) => {
            const s = PRODUCT_STYLE[p.slug] || DEFAULT_STYLE;
            return (
              <Link
                key={p.slug}
                to={`/venturaz-money/product/${p.slug}`}
                className="group rounded-2xl p-6 border border-[#B8860B]/20 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(122,78,29,0.15)] transition-all"
                style={{ background: s.tint }}
                data-testid={`debt-product-${p.slug}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-md inline-flex items-center justify-center" style={{ background: s.accent, color: "#FFF3D9" }}>
                    <Landmark size={16} />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: s.accent }}>
                    From {p.rate_from}% p.a.
                  </div>
                </div>
                <div className="font-heading text-ink text-xl leading-tight mb-1.5">{p.title}</div>
                <div className="text-[13px] text-ink-soft leading-relaxed mb-5">{p.tagline}</div>
                <div className="flex items-center justify-between text-[11px] font-mono text-ink-muted">
                  <span>{fmtINR(p.min_amount)} – {fmtINR(p.max_amount)}</span>
                  <span className="inline-flex items-center gap-1 text-[#7A4E1D] group-hover:underline">
                    Details <ArrowRight size={11} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}
